<!-- Account Already Registered Screen -->
<ng-container
  *ngIf="showAccountAlreadyRegistered"
  [ngTemplateOutlet]="accountAlreadyRegisteredTemplate"
></ng-container>

<dx-validation-group
  #registerDetailsValidationGroup
  *ngIf="!showAccountAlreadyRegistered"
>
  <div class="register-details-form-wrapper">
    <!-- Error Message -->
    <div
      *ngIf="showErrorMessage"
      class="error-message-container"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <p class="error-message">{{ errorMessage }}</p>
    </div>

    <!-- Subtitle -->
    <div class="form-title-container">
      <p class="form-subtitle">
        Your email address will be your username for the Resident Portal.
      </p>
    </div>

    <!-- Email Input -->
    <div class="register-input-wrapper">
      <label for="txtEmail" class="input-label">
        Email
        <span class="required-asterisk">*</span>
      </label>
      <dx-text-box
        id="txtEmail"
        placeholder="Email"
        [(value)]="userInfo.email"
        class="email-input"
        validationMessageMode="always"
      >
        <dx-validator>
          <dxi-validation-rule
            type="required"
            message="Email is required"
          ></dxi-validation-rule>
          <dxi-validation-rule
            type="pattern"
            [pattern]="emailPattern"
            message="Invalid email address"
          ></dxi-validation-rule>
        </dx-validator>
      </dx-text-box>
    </div>

    <!-- Confirm Email Input -->
    <div class="register-input-wrapper">
      <label for="txtConfirmEmail" class="input-label">
        Confirm Email
        <span class="required-asterisk">*</span>
      </label>
      <dx-text-box
        id="txtConfirmEmail"
        placeholder="Confirm Email"
        class="email-input"
        validationMessageMode="always"
      >
        <dx-validator>
          <dxi-validation-rule
            type="required"
            message="Confirm Email is required"
          ></dxi-validation-rule>
          <dxi-validation-rule
            type="pattern"
            [pattern]="emailPattern"
            message="Invalid Confirm Email address"
          ></dxi-validation-rule>
          <dxi-validation-rule
            type="compare"
            [comparisonTarget]="emailComparison"
            comparisonType="==="
            message="Email and Confirm Email do not match"
          ></dxi-validation-rule>
        </dx-validator>
      </dx-text-box>
    </div>

    <!-- Password Input -->
    <div class="register-input-wrapper">
      <label for="txtPassword" class="input-label">
        Password
        <span class="required-asterisk">*</span>
      </label>
      <dx-text-box
        id="txtPassword"
        placeholder="Password"
        ccPreventCopyPaste
        [(value)]="userInfo.password"
        [(mode)]="passwordMode"
        [inputAttr]="{ autocomplete: 'new-password' }"
        [maxLength]="30"
        (onKeyUp)="runPasswordValidation($event)"
        class="password-input"
        validationMessageMode="always"
      >
        <dxi-button
          *ngIf="passwordMode === 'password'"
          name="password"
          location="after"
          [options]="passwordShowButton"
        ></dxi-button>
        <dxi-button
          *ngIf="passwordMode !== 'password'"
          name="password"
          location="after"
          [options]="passwordHideButton"
        ></dxi-button>
        <dx-validator>
          <dxi-validation-rule
            type="required"
            message="Password is required"
          ></dxi-validation-rule>
        </dx-validator>
      </dx-text-box>
    </div>

    <!-- Confirm Password Input -->
    <div class="register-input-wrapper">
      <label for="txtConfirmPassword" class="input-label">
        Confirm Password
        <span class="required-asterisk">*</span>
      </label>
      <dx-text-box
        id="txtConfirmPassword"
        placeholder="Confirm Password"
        ccPreventCopyPaste
        [(mode)]="passwordMode"
        [inputAttr]="{ autocomplete: 'new-password' }"
        [maxLength]="30"
        class="password-input"
        validationMessageMode="always"
      >
        <dxi-button
          *ngIf="passwordMode === 'password'"
          name="cpassword"
          location="after"
          [options]="passwordShowButton"
        ></dxi-button>
        <dxi-button
          *ngIf="passwordMode !== 'password'"
          name="cpassword"
          location="after"
          [options]="passwordHideButton"
        ></dxi-button>
        <dx-validator>
          <dxi-validation-rule
            type="required"
            message="Confirm Password is required"
          ></dxi-validation-rule>
          <dxi-validation-rule
            type="compare"
            [comparisonTarget]="passwordComparison"
            comparisonType="==="
            message="Password and Confirm Password do not match"
          ></dxi-validation-rule>
        </dx-validator>
      </dx-text-box>
    </div>

    <!-- Password Rules -->
    <div class="password-rules-wrapper">
      <ul class="password-rules" role="list" aria-label="Password requirements">
        <li *ngFor="let rule of passwordRules">
          <i
            [ngClass]="[
              iconCircleCheck,
              rule.isValid ? 'rule-valid' : 'rule-invalid'
            ]"
          ></i>
          <span [ngClass]="{ 'rule-text-invalid': !rule.isValid }">
            {{ rule.label }}
          </span>
        </li>
      </ul>
    </div>

    <!-- Password Strength -->
    <div class="password-strength-wrapper">
      <label class="strength-label">
        Password Strength {{ getPasswordStrengthText(passwordStrength / 5) }}
      </label>
      <dx-progress-bar
        #passStrengthSuccess
        [min]="0"
        [max]="5"
        [showStatus]="false"
        class="password-strength-bar"
      ></dx-progress-bar>
    </div>

    <!-- reCAPTCHA -->
    <div class="recaptcha-wrapper">
      <re-captcha
        #reCaptcha
        [siteKey]="recaptchaSiteKey"
        errorMode="handled"
        (resolved)="onRecaptchaResolved($event)"
        (errored)="onRecaptchaErrored()"
      ></re-captcha>
      <p *ngIf="recaptchaErrorMessage" class="recaptcha-error-message">
        {{ recaptchaErrorMessage }}
      </p>
    </div>

    <!-- Back Button -->
    <div class="back-button-container">
      <dx-button
        #btnBack
        text="Back"
        type="default"
        class="back-button"
        (onClick)="btnBackClick()"
      ></dx-button>
    </div>

    <!-- Complete Registration Button -->
    <div class="register-button-wrapper">
      <dx-button
        #btnCompleteRegistration
        text="Continue"
        type="default"
        class="register-button"
        (onClick)="btnCompleteRegistrationClick()"
      ></dx-button>
    </div>

    <!-- OR Divider -->
    <div class="divider-container" *ngIf="showGoogleAuth">
      <div class="divider-line"></div>
      <span class="divider-text">OR</span>
      <div class="divider-line"></div>
    </div>

    <!-- Continue with Google Button -->
    <div class="google-button-container" *ngIf="showGoogleAuth">
      <dx-button
        class="google-button"
        type="default"
        (onClick)="btnGoogleSignupClick()"
      >
        <div class="google-button-content">
          <app-icon
            name="google-logo"
            size="16px"
            class="google-logo"
            ariaLabel="Google logo"
          ></app-icon>
          <span>Continue with Google</span>
        </div>
      </dx-button>
    </div>

    <!-- Sign In Link -->
    <div class="signin-container" [class.no-google-auth]="!showGoogleAuth">
      <span>Already have an account?</span>
      <a href="javascript:void(0)" (click)="btnSignInClick($event)">Sign in</a>
    </div>
  </div>
</dx-validation-group>

<dx-load-panel
  #loadPanelLogin
  shadingColor="rgba(0,0,0,0.4)"
  [position]="{ of: '#loginContainer' }"
  [(visible)]="loadingVisible"
  [showIndicator]="true"
  [showPane]="true"
  [shading]="true"
  [hideOnOutsideClick]="false"
></dx-load-panel>

<cc-message-box #messageBox></cc-message-box>

<!-- Template: Account Already Registered Screen -->
<ng-template #accountAlreadyRegisteredTemplate>
  <div class="account-registered-wrapper">
    <div class="account-registered-content">
      <div class="account-registered-icon">
        <i class="fa-solid fa-user"></i>
        <i class="fa-solid fa-exclamation exclamation-icon"></i>
      </div>

      <p class="account-registered-message">
        It looks like your account has already been registered in the Resident
        Portal. Please
        <button
          type="button"
          class="link-button signin-link"
          (click)="btnSignInClick($event)"
        >
          sign in
        </button>
        with your credentials.
      </p>

      <div class="account-registered-links">
        <div class="link-row">
          <span>
            Forgot your password?
            <button
              type="button"
              class="link-button action-link"
              (click)="btnForgotPassword()"
            >
              Reset now
            </button>
          </span>
        </div>
        <div class="link-row">
          <span>
            Forgot your username or need to validate your email address?
            <button
              type="button"
              class="link-button action-link"
              (click)="btnForgotUsername()"
            >
              Retrieve now
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</ng-template>
